/**
 * @file Flow de réservation de table agnostique de l'hôte.
 * @module packages.common.components.table-reservation.ReservationFlow
 *
 * Cœur partagé du parcours de réservation : service/convives, date, créneau,
 * formule (conditionnel), contact, détails, paiement (conditionnel) et
 * confirmation. Le composant ne connaît ni la source de données (injectée via
 * `ReservationDataProvider`), ni le transport i18n, ni le chrome de l'hôte.
 *
 * Tout ce qui est spécifique à l'hôte arrive par props :
 * - données déjà résolues (`config`, `companyDetails`, `primaryColor`, `companyId`) ;
 * - traduction injectée (`t`, `getCustomText`, `currentLanguage`) ;
 * - chrome via slots (`headerSlot`, `footerSlot`) et callbacks (`onClose`,
 *   `onContentResize`, `containerRef`) ;
 * - contexte d'affichage (`embedded`, `displayMode`) et titres résolus
 *   (`title`, `confirmationTitle`).
 *
 * Côté widget, l'hôte fournit le `LanguageSelector` en `headerSlot`, le
 * `BrandingFooter` en `footerSlot`, `updateHeight` en `onContentResize` et la
 * fermeture iframe en `onClose`. Le site public fournira les équivalents de
 * page (navigation, pas de resize, sélecteur de langue global).
 */
import React, {useCallback, useEffect} from 'react';
import PropTypes from 'prop-types';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Fade from '@mui/material/Fade';
import CloseIcon from '@mui/icons-material/Close';

import {useElementHeight} from 'packages/common/hooks/useElementHeight';
import {useReservationSteps} from './hooks/useReservationSteps';
import {useReservationHandlers} from './hooks/useReservationHandlers';
import {usePaymentRequirement} from './hooks/usePaymentRequirement';
import {useFormulaRequirement} from './hooks/useFormulaRequirement';
import {useRoomSelection} from './hooks/useRoomSelection';
import {usePrepareStripeIntent} from './hooks/usePrepareStripeIntent';
import {useReservationDraftSync} from './hooks/useReservationDraftSync';
import {usePaymentSignalTracker} from './hooks/usePaymentSignalTracker';
import {PAYMENT_ERROR_STAGES} from './utils/paymentSignal';
import {BASE_STEPS} from './constants/steps';
import {CONFIRMED_RESERVATION_STATUSES} from './constants/reservation';
import {ReservationBreadcrumb} from './components/ReservationBreadcrumb';
import {StepActions} from './components/StepActions';
import {renderStepContent} from './utils/stepContentRenderer';
import {
  WidgetContainer,
  WidgetHeader,
  FixedHeaderContainer,
  HeaderSpacerBox,
  ScrollableContentBox,
  FixedFooterContainer,
} from './styles/ReservationFlowStyles';

// Étapes où le préchargement de l'intent Stripe est actif. CONTACT et DETAILS
// laissent le temps à Stripe de répondre pendant que l'utilisateur tape ses
// infos ; PAYMENT est inclus pour permettre un retry implicite si le
// préchargement amont a échoué.
const PREFETCH_STEPS = [BASE_STEPS.CONTACT, BASE_STEPS.DETAILS, BASE_STEPS.PAYMENT];

/**
 * Orchestration et rendu du parcours de réservation, indépendant de l'hôte.
 *
 * @param {Object} props
 * @param {Object} props.config - Configuration du restaurant déjà résolue
 * @param {Object} [props.companyDetails] - Détails de l'établissement
 * @param {string} [props.primaryColor] - Couleur primaire du thème
 * @param {string} [props.companyId] - Identifiant de l'établissement (DateStep/TimeStep)
 * @param {Function} props.t - Fonction de traduction générique (signature i18next)
 * @param {Function} props.getCustomText - Résolution des textes personnalisés du restaurant
 * @param {string} [props.currentLanguage] - Langue courante, propagée au payload de réservation
 * @param {React.ReactNode} [props.headerSlot] - Contenu inséré dans l'en-tête (ex. sélecteur de langue)
 * @param {React.ReactNode} [props.footerSlot] - Contenu inséré dans le pied de page (ex. branding)
 * @param {Function} [props.onClose] - Fermeture du flow (iframe flottante / navigation)
 * @param {Function} [props.onContentResize] - Notification de changement de hauteur du contenu
 * @param {React.Ref} [props.containerRef] - Ref du conteneur pour la mesure de hauteur
 * @param {boolean} [props.embedded=false] - Rendu embarqué dans une page hôte (iframe)
 * @param {'fixed'|'floating'|'page'} [props.displayMode='fixed'] - Mode d'affichage
 * @param {string} props.title - Titre affiché dans l'en-tête
 * @param {string} props.confirmationTitle - Titre affiché à l'étape de confirmation
 * @return {JSX.Element}
 */
export function ReservationFlow({
  config,
  companyDetails,
  primaryColor,
  companyId,
  t,
  getCustomText,
  currentLanguage,
  headerSlot = null,
  footerSlot = null,
  onClose,
  onContentResize,
  containerRef,
  embedded = false,
  displayMode = 'fixed',
  title,
  confirmationTitle,
}) {
  const {
    currentStep,
    maxStepReached,
    formData,
    validationErrors,
    availableSteps,
    isLastStep,
    updateFormData,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    rewindToStep,
    resetSteps,
    setValidationFunction,
    setErrors,
    STEPS,
  } = useReservationSteps();

  const isConfirmationStep = currentStep === STEPS.CONFIRMATION;
  const isFloatingClosable = embedded && displayMode === 'floating';

  // Le titre de confirmation fourni par l'hôte affirme « réservation confirmée ».
  // Quand la confirmation automatique est désactivée, le backend crée la réservation
  // en `pending` (demande à valider) : on remplace alors le titre de l'en-tête pour
  // rester cohérent avec le corps de l'étape de confirmation. Une inscription en
  // liste d'attente n'est pas une réservation : l'en-tête reprend le titre du corps.
  const isWaitlistConfirmation = isConfirmationStep && !!formData.isWaitlistReservation;
  const isPendingConfirmation = isConfirmationStep &&
    !formData.isWaitlistReservation &&
    !!formData.reservationStatus &&
    !CONFIRMED_RESERVATION_STATUSES.includes(formData.reservationStatus);
  let resolvedConfirmationTitle = confirmationTitle;
  if (isWaitlistConfirmation) {
    resolvedConfirmationTitle = t('widgets.reservation.confirmation.waitlistConfirmed');
  } else if (isPendingConfirmation) {
    resolvedConfirmationTitle = t('widgets.reservation.confirmation.pending');
  }

  // Les hooks empreinte et formule sont des effets de bord qui écrivent dans
  // `formData` (paymentRequired, availableFormulas, formulasRequired…).
  // Les deux se déclenchent automatiquement sur chaque changement pertinent.
  //
  // `usePaymentRequirement` garde `paymentRequired` stable pendant la vérif
  // (l'ancienne valeur reste jusqu'à la réponse serveur) — `availableSteps`
  // ne flicker pas, donc le bouton "Suivant" reste cliquable en milieu de
  // flow sans risque. `isChecking` n'est utilisé que pour gater la
  // **dernière** étape (Confirmer) : sur DETAILS/PAYMENT, on attend la
  // confirmation serveur avant de soumettre, pour éviter qu'un clic ultra-
  // rapide après modif amont n'envoie une résa avec un `paymentRequired`
  // obsolète (le backend rejetterait, mauvaise UX).
  //
  // `useFormulaRequirement` reset `formulasRequired` à chaque changement
  // service/date, donc le gate sur toutes les étapes reste nécessaire pour
  // ne pas sauter FormulaStep par erreur.
  const {isChecking: isCheckingPayment} = usePaymentRequirement(formData, updateFormData);
  const {isChecking: isCheckingFormula} = useFormulaRequirement(formData, updateFormData);

  // Amorce le choix de salle : charge les salles réservables (si le restaurant a
  // activé l'option) et décide si l'étape ROOM doit exister. La disponibilité par
  // salle pour la date choisie est recalculée dans RoomStep.
  useRoomSelection(config, updateFormData);

  // Synchronise l'activation des champs optionnels (Détails supplémentaires)
  // depuis la configuration du restaurant vers le formData, pour que le calcul
  // des étapes puisse retirer l'étape Détails si tout est désactivé.
  useEffect(() => {
    if (config?.optionalFields) {
      updateFormData('optionalFields', config.optionalFields);
    }
  }, [config?.optionalFields, updateFormData]);

  // Signal empreinte du brouillon : sous-états déduits du formData, erreurs
  // et tentatives enregistrées par les hooks ci-dessous. Voyage avec le sync.
  const {recordPaymentError, recordPaymentAttempt, recordCardValidated} = usePaymentSignalTracker({formData, currentStep, updateFormData});
  const handleCardInputError = useCallback(
      (error) => recordPaymentError(PAYMENT_ERROR_STAGES.CARD_INPUT, error),
      [recordPaymentError],
  );

  // Préchargement de l'intent Stripe dès CONTACT : à partir de cette étape,
  // les paramètres qui impactent l'empreinte (service, date, créneau, convives,
  // formules) sont figés. La latence Stripe (~300-600 ms) est absorbée pendant
  // que l'utilisateur remplit ses informations personnelles ; quand il arrive
  // sur PAYMENT, le `clientSecret` est déjà dans `paymentConfig`.
  //
  // La fenêtre inclut PAYMENT pour permettre un retry implicite si le
  // préchargement a échoué sur une étape antérieure (le hook ré-essaie au
  // changement d'étape grâce à `currentStep` dans ses deps).
  const {prepareError} = usePrepareStripeIntent({
    formData,
    updateFormData,
    currentStep,
    enabledAtSteps: PREFETCH_STEPS,
    t,
    onPrepareError: recordPaymentError,
    currentLanguage,
  });

  // Trace fire-and-forget des parcours non finalisés (brouillon backend) dès
  // que des coordonnées valides sont saisies à l'étape CONTACT. Strictement
  // non bloquant ; inerte si l'adapter hôte n'expose pas syncReservationDraft.
  useReservationDraftSync({formData, currentStep, currentLanguage});

  const {
    isStepReady,
    setIsStepReady,
    submitLoading,
    handleComplete,
    handleReset,
    handleChooseAnotherDate,
    provideValidationFunction,
    handleSetErrors,
    handleNextStep,
  } = useReservationHandlers({
    formData,
    config,
    STEPS,
    isLastStep,
    currentLanguage,
    updateFormData,
    goToStep,
    rewindToStep,
    goToNextStep,
    resetSteps,
    setErrors,
    setValidationFunction,
    recordPaymentError,
    recordPaymentAttempt,
    recordCardValidated,
  });

  // L'écran « créneau perdu » porte sa propre issue : ni fil d'Ariane cliquable,
  // ni boutons Précédent/Confirmer tant qu'il est affiché
  const hasSteps = !isConfirmationStep && !formData.slotLost && availableSteps.mapping[currentStep] !== undefined;
  const hasFooter = !isConfirmationStep && !formData.slotLost;

  // En embarqué, en-tête et pied sortent du flux : leur place est rendue par un
  // espaceur et une marge basse, calés sur leur hauteur réelle plutôt que sur
  // une constante qui laisserait une bande blanche ou masquerait du contenu.
  const [headerRef, headerHeight] = useElementHeight(embedded);
  const [footerRef, footerHeight] = useElementHeight(embedded && hasFooter);

  // La réserve qui s'ajuste déplace le contenu : l'hôte doit réviser sa hauteur.
  useEffect(() => {
    if (embedded) onContentResize?.();
  }, [embedded, headerHeight, footerHeight, onContentResize]);

  return (
    <Box sx={{
      // En mode page (site public), le fond reste transparent pour que la carte
      // du flow repose directement sur le fond de la page hôte — pas de panneau
      // blanc autour. En embarqué/standalone widget, fond blanc comme avant.
      backgroundColor: displayMode === 'page' ? 'transparent' : 'background.paper',
      minHeight: '100%',
      ...(!embedded && displayMode !== 'page' && {
        paddingTop: 4,
      }),
    }}>
      <WidgetContainer
        embedded={embedded}
        displayMode={displayMode}
        ref={containerRef}
        maxWidth="512px"
        maxHeight="100%"
      >
        <FixedHeaderContainer ref={headerRef} embedded={embedded} displayMode={displayMode}>
          <WidgetHeader primaryColor={primaryColor}>
            <Typography variant="subtitle1" sx={{fontWeight: 'bold', flexGrow: 1}}>
              {isConfirmationStep ? resolvedConfirmationTitle : title}
            </Typography>

            {headerSlot}

            {isFloatingClosable && onClose && (
              <IconButton
                size="small"
                onClick={onClose}
                sx={{color: 'inherit'}}
                aria-label={t('common.buttons.close')}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </WidgetHeader>
          {hasSteps && (
            <ReservationBreadcrumb
              currentStep={currentStep}
              maxStepReached={maxStepReached}
              availableSteps={availableSteps}
              formData={formData}
              primaryColor={primaryColor}
              onStepClick={goToStep}
              t={t}
            />
          )}
        </FixedHeaderContainer>

        <HeaderSpacerBox hasSteps={hasSteps} embedded={embedded} measuredHeight={headerHeight} />

        <ScrollableContentBox
          hasFooter={hasFooter}
          embedded={embedded}
          displayMode={displayMode}
          footerHeight={footerHeight}
        >
          <Box key={formData.slotLost ? `${currentStep}-slot-lost` : currentStep} sx={{width: '100%'}}>
            <Fade in timeout={400} onEntered={onContentResize}>
              <Box>
                {renderStepContent({
                  currentStep,
                  STEPS,
                  formData,
                  updateFormData,
                  config,
                  validationErrors,
                  provideValidationFunction,
                  handleSetErrors,
                  setIsStepReady,
                  handleComplete,
                  handleReset,
                  handleCloseWidget: onClose,
                  handleNextStep,
                  companyId,
                  displayMode,
                  embedded,
                  availableSteps,
                  primaryColor,
                  companyDetails,
                  prepareError,
                  onCardInputError: handleCardInputError,
                  handleChooseAnotherDate,
                  onContentResize,
                  t,
                  getCustomText,
                })}
              </Box>
            </Fade>
          </Box>
        </ScrollableContentBox>

        {hasFooter && (
          <FixedFooterContainer ref={footerRef} embedded={embedded} displayMode={displayMode}>
            <StepActions
              showBack={currentStep !== STEPS.SERVICE_GUESTS}
              onBack={goToPreviousStep}
              onNext={handleNextStep}
              isLastStep={isLastStep}
              isNextLoading={isLastStep && submitLoading}
              disableNext={!isStepReady || isCheckingFormula || (isLastStep && isCheckingPayment)}
              nextLabel={
              currentStep === STEPS.FORMULA &&
              !config?.formulaRequired &&
              !(formData.availableFormulas || []).some(
                  (formula) => formula.availability?.requiresSelection === true,
              ) &&
              !(formData.selectedFormulas?.length > 0) ?
                t('widgets.reservation.form.formulaContinueWithout') :
                // Choix de salle facultatif et aucune salle choisie : le bouton « Suivant »
                // propose de poursuivre sans préférence (même pattern que les formules).
                currentStep === STEPS.ROOM &&
                config?.allowRoomSelection &&
                config?.roomSelectionRequired === false &&
                !formData.selectedAreaId ?
                  t('widgets.reservation.form.roomContinueWithout') :
                  undefined
              }
              t={t}
            />
            {footerSlot}
          </FixedFooterContainer>
        )}
      </WidgetContainer>
    </Box>
  );
}

ReservationFlow.propTypes = {
  config: PropTypes.object,
  companyDetails: PropTypes.object,
  primaryColor: PropTypes.string,
  companyId: PropTypes.string,
  t: PropTypes.func.isRequired,
  getCustomText: PropTypes.func,
  currentLanguage: PropTypes.string,
  headerSlot: PropTypes.node,
  footerSlot: PropTypes.node,
  onClose: PropTypes.func,
  onContentResize: PropTypes.func,
  containerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({current: PropTypes.any}),
  ]),
  embedded: PropTypes.bool,
  displayMode: PropTypes.oneOf(['fixed', 'floating', 'page']),
  title: PropTypes.string,
  confirmationTitle: PropTypes.string,
};
